// ─── La caparra della fascia serale (25/09/2026, fase 1) ─────────────────────
//
// 🗣️ Dal documento del committente «Caparra Premium con SumUp»: da lunedì a
// venerdì le partite che iniziano fra le 18 e le 22 chiedono una caparra di
// 30 €, che poi si scala dal totale. Disdetta oltre 48 ore prima ⇒ torna nel
// borsellino come credito, che vale 12 mesi; dopo, o se non ci si presenta, la
// caparra resta al circolo.
//
// ⭐ Ai soci si dice «fascia serale», mai «Premium» (dal mockup approvato).
//
// Qui stanno le regole PURE — quale ora è serale, come si consuma il credito
// che scade, come si divide la caparra fra i giocatori — e i contratti. Le
// scritture stanno nell'API (`modules/caparra/`).
import { z } from 'zod';
import { dataSchema, oraSchema } from './circolo.js';
// ── Le impostazioni ─────────────────────────────────────────────────────────
/**
 * 🌅 Fra quali ore può stare «Mai prima delle» (26/09/2026). Sotto le 5 è
 * notte per chiunque; sopra mezzogiorno l'avviso del giorno stesso, per una
 * partita del mattino, non arriverebbe più.
 */
export const NON_PRIMA_DELLE_MIN = '05:00';
export const NON_PRIMA_DELLE_MAX = '12:00';
export const fasciaSeraleSchema = z.object({
    /** 🕰️ 0 = domenica … 6 = sabato, come `bookable_slots`. */
    giorni: z.array(z.number().int().min(0).max(6)).min(1),
    /** L'ora d'INIZIO della partita, compresa, fra `dalle` e `alle`. */
    dalle: oraSchema,
    alle: oraSchema,
});
export const impostazioniCaparraSchema = z.object({
    /** ⛔ Spenta, il bot prenota come prima. La accende la Segreteria. */
    attiva: z.boolean(),
    /**
     * Vale per le prenotazioni FATTE da questo giorno in poi: quelle che esistono
     * già non chiedono niente (§ 12 del documento). Nullo = da subito.
     */
    dal: dataSchema.nullable(),
    importoCents: z.number().int().min(100).max(100_000),
    oreDisdetta: z
        .number()
        .int()
        .min(1)
        .max(24 * 14),
    minutiPerPagare: z.number().int().min(5).max(120),
    /**
     * 🆕 26/09/2026 (fase 2) — **Il link della Segreteria: quanto si aspetta il
     * tocco.** Dal committente: i 15 minuti per pagare partono da quando il socio
     * tocca «Paga la caparra»; se non lo tocca entro questi minuti dall'arrivo
     * del messaggio, il campo torna libero. *«1 sì 60»*.
     */
    minutiPerToccare: z
        .number()
        .int()
        .min(10)
        .max(24 * 60)
        .default(60),
    // 🔄 Voce 36 (27/09/2026): `mesiCredito` e `giorniAvvisoScadenza` non stanno
    // più qui ⇒ `crediti.ts`, una regola sola con quelli del premio.
    fascia: z.array(fasciaSeraleSchema),
    /**
     * 🆕 Fase 2 — gli avvisi all'organizzatore sulla partita (§ 4.4): il giorno
     * prima a quest'ora, se è incompleta; il giorno stesso tante ore prima, mai
     * prima di `nonPrimaDelle`.
     *
     * 🔄 26/09/2026 — `nonPrimaDelle` lo decide il circolo (prima era scritto nel
     * bot, alle 8). 🗣️ *«Voglio deciderlo io … se dobbiamo decidere un fisso
     * sono le 7 di mattina»*. Chi ha salvato le impostazioni prima non lo ha: vale
     * 07:00.
     */
    avvisi: z
        .object({
        vigiliaAlle: oraSchema,
        orePrima: z.number().int().min(1).max(12),
        nonPrimaDelle: oraSchema
            .refine((h) => h >= NON_PRIMA_DELLE_MIN && h <= NON_PRIMA_DELLE_MAX, {
            message: `Fra le ${NON_PRIMA_DELLE_MIN} e le ${NON_PRIMA_DELLE_MAX}`,
        })
            .default('07:00'),
    })
        .default({ vigiliaAlle: '18:00', orePrima: 3, nonPrimaDelle: '07:00' }),
    /**
     * 🆕 Fase 2 — R16: dopo `quanti` no-show senza caparra in `mesi` mesi, la
     * caparra si chiede su ogni prenotazione per `mesiCaparra` mesi.
     */
    noShow: z
        .object({
        quanti: z.number().int().min(1).max(10),
        mesi: z.number().int().min(1).max(36),
        mesiCaparra: z.number().int().min(1).max(24),
    })
        .default({ quanti: 2, mesi: 12, mesiCaparra: 3 }),
    /**
     * 🆕 27/09/2026 — **Il campo fisso chiesto al bot** (mockup «Fisso dal bot»,
     * approvato). 🗣️ *«il campo fisso lo farei per due mesi, poi si può sempre
     * cambiare in impostazioni … lo farei creare subito»*. Ogni settimana, alla
     * stessa ora e sullo stesso campo, per al massimo `mesiMax` mesi; gli altri
     * ritmi e le durate più lunghe restano alla Segreteria. Chi non l'ha salvato
     * lo ha acceso, a due mesi.
     */
    fissoDalBot: z
        .object({
        attivo: z.boolean(),
        mesiMax: z.number().int().min(1).max(6),
    })
        .default({ attivo: true, mesiMax: 2 }),
    regolamento: z.object({
        /** La pagina col testo approvato dal direttivo. Senza, il bot non la può mostrare. */
        url: z.string().trim().url().startsWith('https://', 'Serve un indirizzo https://').nullable(),
        /** Cambiandola, il bot la richiede ad accettare a tutti. */
        versione: z.string().trim().min(1).max(40),
    }),
});
export const IMPOSTAZIONI_CAPARRA_DI_PARTENZA = {
    attiva: false,
    dal: null,
    importoCents: 3000,
    oreDisdetta: 48,
    minutiPerPagare: 15,
    minutiPerToccare: 60,
    avvisi: { vigiliaAlle: '18:00', orePrima: 3, nonPrimaDelle: '07:00' },
    noShow: { quanti: 2, mesi: 12, mesiCaparra: 3 },
    fascia: [
        { giorni: [1, 2, 3, 4], dalle: '18:00', alle: '21:00' },
        { giorni: [5], dalle: '18:00', alle: '22:00' },
    ],
    fissoDalBot: { attivo: true, mesiMax: 2 },
    regolamento: { url: null, versione: '1' },
};
/** Il giorno della settimana di una data, senza passare da un fuso (0 = domenica). */
export function giornoDellaSettimana(data) {
    return new Date(`${data}T12:00:00Z`).getUTCDay();
}
/**
 * ⭐ È in fascia serale? Conta l'ora d'**inizio**, non un elenco di turni: una
 * partita che inizia alle 18:30 lo è anche se quel turno oggi non c'è (§ 2.1).
 */
export function eFasciaSerale(fasce, data, ora) {
    const g = giornoDellaSettimana(data);
    return fasce.some((f) => f.giorni.includes(g) && ora >= f.dalle && ora <= f.alle);
}
/**
 * ⭐⭐ **Il credito che scade si consuma per primo, a cominciare da quello che
 * scade prima** (§ 2.3, FIFO sulle scadenze).
 *
 * ⚖️ Si RICALCOLA dalla storia, non si tiene scritto da nessuna parte: il saldo
 * resta la somma nuda di sempre (`member_wallet_balance`), e questa funzione
 * dice soltanto **di che denaro** è fatto. Una seconda colonna «credito
 * residuo» sarebbe un secondo numero sullo stesso denaro, e un giorno i due
 * direbbero cose diverse.
 *
 * Le regole, in ordine di tempo:
 *   · un'entrata con la scadenza apre un lotto; senza, è denaro libero;
 *   · la scadenza di un lotto (`lotTransactionId`) lo chiude;
 *   · lo storno di un'uscita restituisce **esattamente** ciò che quell'uscita
 *     aveva preso, lotti compresi: annullare un pagamento non allunga la vita al
 *     credito, e non gliela accorcia;
 *   · ogni altra uscita prende dai lotti ancora vivi in quell'istante, prima
 *     quello che scade prima, poi dal denaro libero.
 */
export function lottiDelBorsellino(movimenti) {
    const ordinati = [...movimenti].sort((a, b) => Date.parse(a.recordedAt) - Date.parse(b.recordedAt) || a.id.localeCompare(b.id));
    const lotti = new Map();
    let libero = 0;
    /** Cosa ha preso ogni uscita, per poterlo restituire allo storno. */
    const prese = new Map();
    const prendi = (id, quanto, istante) => {
        const vivi = [...lotti.values()]
            .filter((l) => l.remainingCents > 0 && Date.parse(l.expiresAt) > istante)
            .sort((a, b) => Date.parse(a.expiresAt) - Date.parse(b.expiresAt) || a.id.localeCompare(b.id));
        const presa = { lotti: [], libero: 0 };
        let resta = quanto;
        for (const l of vivi) {
            if (resta === 0)
                break;
            const x = Math.min(resta, l.remainingCents);
            l.remainingCents -= x;
            resta -= x;
            presa.lotti.push([l.id, x]);
        }
        libero -= resta;
        presa.libero = resta;
        prese.set(id, presa);
    };
    for (const m of ordinati) {
        const istante = Date.parse(m.recordedAt);
        if (m.direction === 'charge') {
            if (m.lotTransactionId) {
                const l = lotti.get(m.lotTransactionId);
                if (l) {
                    const x = Math.min(m.amountCents, l.remainingCents);
                    l.remainingCents -= x;
                    libero -= m.amountCents - x;
                }
                else
                    libero -= m.amountCents;
                continue;
            }
            if (m.reversesTransactionId && lotti.has(m.reversesTransactionId)) {
                // Lo storno di un credito che scade: si toglie da quel lotto.
                const l = lotti.get(m.reversesTransactionId);
                const x = Math.min(m.amountCents, l.remainingCents);
                l.remainingCents -= x;
                libero -= m.amountCents - x;
                continue;
            }
            prendi(m.id, m.amountCents, istante);
            continue;
        }
        // Entrate: ricarica o rimborso.
        const storna = m.reversesTransactionId ? prese.get(m.reversesTransactionId) : undefined;
        if (storna) {
            for (const [id, x] of storna.lotti) {
                const l = lotti.get(id);
                if (l)
                    l.remainingCents += x;
            }
            libero += m.amountCents - storna.lotti.reduce((s, [, x]) => s + x, 0);
            continue;
        }
        if (m.expiresAt) {
            lotti.set(m.id, {
                id: m.id,
                amountCents: m.amountCents,
                remainingCents: m.amountCents,
                expiresAt: m.expiresAt,
            });
        }
        else
            libero += m.amountCents;
    }
    return {
        lotti: [...lotti.values()].sort((a, b) => Date.parse(a.expiresAt) - Date.parse(b.expiresAt)),
        liberoCents: libero,
    };
}
/** Quanto credito che scade è ancora vivo adesso, e quanto è già scaduto e aspetta la sua riga. */
export function creditoVivo(l, adesso) {
    let vivo = 0;
    let scaduto = 0;
    for (const x of l.lotti) {
        if (x.remainingCents <= 0)
            continue;
        if (Date.parse(x.expiresAt) > adesso.getTime())
            vivo += x.remainingCents;
        else
            scaduto += x.remainingCents;
    }
    return { vivoCents: vivo, scadutoCents: scaduto };
}
/**
 * ⭐⭐ **Come si scala la caparra** — la scelta A del committente (25/09/2026):
 * prima la quota di chi ha organizzato; quello che avanza **dalle quote degli
 * altri, in parti uguali**. *«La caparra (30 €) è più della quota di Marco
 * (15 €): Anna, Luca e Paolo pagano 10 € a testa.»*
 *
 * ⚖️ In parti uguali ma mai oltre il residuo di ciascuno: chi ha già pagato
 * tutto non riceve niente, e la parte che gli spettava passa agli altri. I
 * centesimi che non si dividono vanno ai primi della fila.
 * ⇒ Torna le quote scalate e l'**avanzo**: quello che non trova nessuna quota
 * (una partita che costa meno della caparra) torna a chi ha organizzato, come
 * credito.
 */
export function ripartisciCaparra(caparraCents, quote) {
    const scalate = new Map();
    let resta = caparraCents;
    const org = quote.find((q) => q.organizzatore);
    if (org) {
        const x = Math.min(resta, org.residuoCents);
        if (x > 0)
            scalate.set(org.playerId, x);
        resta -= x;
    }
    let altri = quote.filter((q) => !q.organizzatore).map((q) => ({ ...q }));
    while (resta > 0) {
        altri = altri.filter((q) => q.residuoCents - (scalate.get(q.playerId) ?? 0) > 0);
        if (altri.length === 0)
            break;
        const parte = Math.floor(resta / altri.length);
        let resto = resta - parte * altri.length;
        let dato = 0;
        for (const q of altri) {
            const libero = q.residuoCents - (scalate.get(q.playerId) ?? 0);
            const voluto = parte + (resto > 0 ? 1 : 0);
            if (resto > 0)
                resto -= 1;
            const x = Math.min(voluto, libero);
            if (x > 0) {
                scalate.set(q.playerId, (scalate.get(q.playerId) ?? 0) + x);
                dato += x;
            }
        }
        if (dato === 0)
            break;
        resta -= dato;
    }
    return {
        scalate: [...scalate.entries()].map(([playerId, cents]) => ({ playerId, cents })),
        avanzoCents: resta,
    };
}
// ── Gli stati ───────────────────────────────────────────────────────────────
export const STATI_CAPARRA = ['pending', 'paid', 'forfeited', 'credited', 'consumed', 'expired'];
export const statoCaparraSchema = z.enum(STATI_CAPARRA);
export const COME_PAGATA = ['card', 'credit', 'mixed', 'desk_cash', 'desk_card'];
export const comePagataSchema = z.enum(COME_PAGATA);
/** Le parole della Segreteria, dal mockup approvato. */
export const ETICHETTE_CAPARRA = {
    pending: 'Attende',
    paid: 'Acconto',
    forfeited: 'Trattenuto',
    credited: 'Nel borsellino',
    consumed: 'Scalato',
    expired: 'Non pagato',
};
// ── Il contratto col bot ────────────────────────────────────────────────────
/**
 * La caparra di una prenotazione, come la legge il bot.
 *
 * 🕰️ Le ore arrivano **già in ora di Roma** (`…Date` + `…Time`): il bot non fa
 * conti di fuso, perché dentro un container il fuso è UTC.
 */
export const caparraDelPonteSchema = z.object({
    depositId: z.uuid(),
    bookingId: z.uuid(),
    status: statoCaparraSchema,
    paidWith: comePagataSchema.nullable(),
    amountCents: z.number().int(),
    creditCents: z.number().int(),
    cardCents: z.number().int(),
    /** ⏳ Fino a quando il campo è tenuto. Nullo quando non si aspetta niente. */
    holdUntil: z.string().nullable(),
    /** I minuti che restano per pagare, arrotondati per eccesso. */
    holdMinutesLeft: z.number().int().nullable(),
    refundableUntilDate: dataSchema,
    refundableUntilTime: oraSchema,
    /** Nata quando mancavano già meno delle ore della disdetta. */
    nonRefundable: z.boolean(),
    /** Le ore della disdetta gratuita, per dirle al socio («mancano meno di 48 ore»). */
    cancelHours: z.number().int(),
    /** Quanti mesi vale il credito, se la caparra torna nel borsellino. */
    creditMonths: z.number().int(),
    /** Una disdetta adesso la farebbe tornare nel borsellino? */
    refundableNow: z.boolean(),
    /** La pagina di SumUp, quando c'è un pagamento con la carta in corso. */
    checkoutUrl: z.string().nullable(),
    /** Credito che il socio può usare adesso (non scaduto). */
    walletUsableCents: z.number().int(),
    /** Quando la caparra è tornata nel borsellino: fino a quando vale. */
    creditExpiresOn: dataSchema.nullable(),
    closeReason: z.string().nullable(),
    /**
     * 🆕 Fase 2 — la caparra chiesta dalla Segreteria col link, e il socio non ha
     * ancora toccato «Paga la caparra»: il tocco fa partire i minuti per pagare
     * (`POST /deposits/:id/start`).
     */
    waitingForTap: z.boolean().default(false),
    /** 🆕 Fase 2 — quando è stata pagata: il promemoria delle 72 ore non va a chi ha appena pagato. */
    paidAt: z.string().nullable().default(null),
    /**
     * 🆕 Fase 3 — a cosa serve: la caparra di una partita (`booking`), quella di chi diventa
     * organizzatore (`change`, R5), o quella di un campo fisso (`series`, R7). Il bot ci sceglie la
     * ricevuta.
     */
    purpose: z.enum(['booking', 'change', 'series']).default('booking'),
    /**
     * 🆕 Fase 3 (R7) — la caparra nuova di un campo fisso dopo una persa: non ha minuti ma un
     * termine (quello della disdetta della prossima partita), e non si annulla dal bot — chi vuole
     * lasciare il fisso non deve fare niente.
     */
    renewal: z.boolean().default(false),
});
/** Prima del «✅ Prenota»: serve la caparra, e quanto? */
export const domandaPreventivoSchema = z.object({
    memberId: z.uuid(),
    date: dataSchema,
    time: oraSchema,
});
export const preventivoCaparraSchema = z.object({
    required: z.boolean(),
    amountCents: z.number().int(),
    holdMinutes: z.number().int(),
    /** Mancano già meno delle ore della disdetta: la caparra nasce non rimborsabile. */
    nonRefundable: z.boolean(),
    refundableUntilDate: dataSchema.nullable(),
    refundableUntilTime: oraSchema.nullable(),
    walletUsableCents: z.number().int(),
    /** Le ore della disdetta gratuita. */
    cancelHours: z.number().int(),
    /** Il regolamento da accettare prima, se il socio non l'ha ancora accettato. */
    terms: z.object({ url: z.string().nullable(), version: z.string() }).nullable(),
    creditMonths: z.number().int(),
    /**
     * 🆕 Fase 2 — perché serve: `sera` (la fascia serale) o `no_show` (R16: il
     * socio ha saltato delle partite, e fino a `flagUntil` la caparra serve
     * sempre). Nullo quando non serve.
     */
    reason: z.enum(['sera', 'no_show']).nullable().default(null),
    flagUntil: dataSchema.nullable().default(null),
    /**
     * 🆕 Voce 51 (mockup «Meno di 48 ore», «1 sì, 2 B») — mancano già meno delle ore della disdetta:
     * da quando si prenota, se poi si annulla o il campo resta vuoto l'acconto resta al circolo. Il bot
     * lo dice nel riepilogo, prima di «✅ Prenota», con le strade per salvarlo. Nessun «sì» in più
     * (`nonRefundable` resta falso).
     */
    withinCancelWindow: z.boolean().default(false),
});
export const pagaCaparraSchema = z.object({
    memberId: z.uuid(),
    /** Usa il credito del borsellino (tutto quello che serve, o quello che c'è). */
    useCredit: z.boolean(),
});
export const chiDellaCaparraSchema = z.object({ memberId: z.uuid() });
export const esitoCaparraSchema = z.object({
    outcome: z.enum(['fatto', 'rifiutato', 'non_lo_so']),
    reason: z.string().nullable(),
    deposit: caparraDelPonteSchema.nullable(),
});
export const regolamentoAccettatoSchema = z.object({ version: z.string().trim().min(1).max(40) });
// ── Il contratto con la Segreteria ──────────────────────────────────────────
/** La caparra nel calendario: quel poco che serve al blocchetto. */
export const caparraInBreveSchema = z.object({
    id: z.uuid(),
    status: statoCaparraSchema,
    paidWith: comePagataSchema.nullable(),
    amountCents: z.number().int(),
    holdUntil: z.string().nullable(),
});
export const caparraDellaSegreteriaSchema = z.object({
    id: z.uuid(),
    bookingId: z.uuid(),
    memberId: z.uuid(),
    memberName: z.string(),
    status: statoCaparraSchema,
    paidWith: comePagataSchema.nullable(),
    origin: z.string(),
    amountCents: z.number().int(),
    creditCents: z.number().int(),
    cardCents: z.number().int(),
    holdUntil: z.string().nullable(),
    refundableUntil: z.string(),
    refundableUntilDate: dataSchema,
    refundableUntilTime: oraSchema,
    nonRefundable: z.boolean(),
    refundableNow: z.boolean(),
    sumupTransactionCode: z.string().nullable(),
    paidAt: z.string().nullable(),
    appliedAt: z.string().nullable(),
    closedAt: z.string().nullable(),
    closeReason: z.string().nullable(),
    closedByEmail: z.string().nullable(),
    started: z.boolean(),
    noShow: z.boolean(),
    /** 🆕 Fase 3 (R7): la caparra del campo fisso, vista da una sua partita. */
    seriesId: z.uuid().nullable().default(null),
    /** 🆕 Fase 3 (R9): la caparra è sul lettore, che aspetta la carta. */
    onReader: z.boolean().default(false),
});
/** 🆕 Fase 3 (R9): il lettore SumUp Solo al banco. */
export const statoDelLettoreSchema = z.object({
    /** C'è tutto per usarlo: la chiave, la Affiliate Key e un lettore collegato. */
    ready: z.boolean(),
    /** Cosa manca, detto alla Segreteria; nullo quando c'è tutto. */
    missing: z.string().nullable(),
    reader: z
        .object({ id: z.string(), name: z.string(), serial: z.string().nullable(), pairedAt: z.string() })
        .nullable(),
});
export const statoSumupSchema = z.object({
    configured: z.boolean(),
    sandbox: z.boolean(),
    merchantCodeHint: z.string().nullable(),
    webhook: z.boolean(),
    lastCheck: z.object({ at: z.string(), ok: z.boolean(), detail: z.string().nullable() }).nullable(),
    reader: statoDelLettoreSchema.default({ ready: false, missing: null, reader: null }),
});
export const pannelloCaparraSchema = z.object({
    settings: impostazioniCaparraSchema,
    sumup: statoSumupSchema,
});
export const motivoObbligatorioSchema = z.object({ reason: z.string().trim().min(3).max(500) });
/** «Campo non utilizzabile» (R1): quali campi, quando, perché. */
export const campoInagibileSchema = z.object({
    courtIds: z.array(z.uuid()).min(1),
    date: dataSchema,
    from: oraSchema,
    to: oraSchema,
    reason: z.string().trim().min(3).max(200),
});
export const prenotazioneColpitaSchema = z.object({
    bookingId: z.uuid(),
    courtName: z.string(),
    time: oraSchema,
    endTime: oraSchema,
    type: z.string(),
    organizer: z.string().nullable(),
    depositCents: z.number().int().nullable(),
});
export const colpiteSchema = z.object({ items: z.array(prenotazioneColpitaSchema) });
/**
 * Come la Segreteria risolve la caparra quando prenota lei una sera (sezione 9).
 * 🆕 Fase 3: `lettore` (R9, la carta sul Solo: la caparra aspetta, e la scheda
 * la manda subito al lettore); `esente` vuole il motivo, che si legge nei
 * resoconti.
 */
export const caparraDallaSegreteriaSchema = z.discriminatedUnion('modo', [
    z.object({ modo: z.literal('link') }),
    z.object({ modo: z.literal('banco'), metodo: z.enum(['cash', 'card']) }),
    z.object({ modo: z.literal('lettore') }),
    z.object({ modo: z.literal('esente'), motivo: z.string().trim().min(3, 'Scrivi il motivo').max(200) }),
]);
/**
 * 🆕 Fase 2 — il socio non è nel bot: il link della caparra non gli può
 * arrivare, e la finestra propone l'invito al bot (regola del committente,
 * 26/09/2026: *«ti mando il link affinché tu puoi farti da solo la prenotazione»*).
 */
export const dettagliCaparraDaScegliereSchema = z.object({
    memberId: z.uuid(),
    memberName: z.string(),
    firstName: z.string().nullable(),
    inBot: z.boolean(),
    reason: z.enum(['sera', 'no_show']),
    flagUntil: dataSchema.nullable(),
    /** I minuti del link: per toccare «Paga la caparra», e poi per pagare. */
    tapMinutes: z.number().int(),
    payMinutes: z.number().int(),
    /** 🆕 Fase 3 (R7): è la caparra di un campo fisso, una sola per tutte le partite. */
    series: z.boolean().default(false),
});
// ── Fase 2: la conferma della partita, i no-show ripetuti ─────────────────
/**
 * Gli avvisi all'organizzatore (§ 4.4), e cosa ha risposto. `chiesta` la segna
 * il bot quando manda la domanda, così la Segreteria vede anche chi non
 * risponde.
 */
export const RISPOSTE_ALLA_CONFERMA = ['chiesta', 'confermata', 'problema', 'ci_pensiamo'];
export const rispostaConfermaSchema = z.enum(RISPOSTE_ALLA_CONFERMA);
export const MOMENTI_DELLA_CONFERMA = ['72h', 'vigilia', 'giorno'];
export const confermaDalPonteSchema = z.object({
    memberId: z.uuid(),
    moment: z.enum(MOMENTI_DELLA_CONFERMA),
    answer: rispostaConfermaSchema,
});
export const ETICHETTE_CONFERMA = {
    chiesta: '❔ non ha risposto',
    confermata: '✅ confermata',
    problema: '⚠️ problema',
    ci_pensiamo: 'ci pensa l’organizzatore',
};
/** ↩️ Voce 30 — un no-show annullato dalla Segreteria: chi, quando e perché. */
export const annulloNoShowSchema = z.object({
    at: z.string(),
    by: z.string().nullable(),
    reason: z.string(),
});
/** R16: il socio che ha saltato troppe partite, e fino a quando la caparra serve sempre. */
export const bloccoNoShowSchema = z.object({
    active: z.boolean(),
    until: dataSchema.nullable(),
    dates: z.array(dataSchema),
    removed: z.object({ at: z.string(), by: z.string().nullable(), reason: z.string() }).nullable(),
    /** 🆕 Voce 30 — le partite saltate senza caparra negli ultimi `mesi` mesi, anche sotto la soglia. */
    recent: z.array(dataSchema).default([]),
    /** 🆕 Voce 30 — i no-show annullati negli ultimi `mesi` mesi: la data della partita, chi, quando e perché. */
    cancelled: z.array(annulloNoShowSchema.extend({ date: dataSchema })).default([]),
    /** 🆕 Voce 30 — i mesi della finestra (Impostazioni › Caparra › «In quanti mesi»), per dirli. */
    months: z.number().int().default(12),
});
/** «2026-09-25» + 3 mesi = «2026-12-25». Il giorno che non esiste si ferma all'ultimo del mese. */
export function piuMesi(data, mesi) {
    const [y, m, g] = data.split('-').map(Number);
    const tot = y * 12 + (m - 1) + mesi;
    const anno = Math.floor(tot / 12);
    const mese = (tot % 12) + 1;
    const ultimo = new Date(Date.UTC(anno, mese, 0)).getUTCDate();
    const due = (n) => String(n).padStart(2, '0');
    return `${anno}-${due(mese)}-${due(Math.min(g, ultimo))}`;
}
/**
 * ⭐ **R16, IN UNA FUNZIONE PURA.** Il lucchetto non si scrive da nessuna parte:
 * si calcola dalle partite saltate, così non c'è niente da tenere allineato.
 *
 * Scatta nel giorno del no-show che porta a `quanti` in `mesi` mesi (contando
 * quel giorno), e dura `mesiCaparra` mesi da lì. ⚖️ Se la Segreteria l'ha tolto
 * DOPO che era scattato, resta tolto: un no-show nuovo lo fa riscattare solo
 * se da solo, con quelli dopo la rimozione, raggiunge di nuovo la soglia.
 *
 * @param saltate le date delle partite saltate senza caparra, in qualunque ordine.
 * @param toltoIl la data (di Roma) dell'ultima rimozione, o nulla.
 */
export function bloccoDaiNoShow(saltate, oggi, regola, toltoIl) {
    const date = [...saltate].sort();
    let inizio = null;
    let quali = [];
    for (let i = 0; i < date.length; i++) {
        const d = date[i];
        const da = piuMesi(d, -regola.mesi);
        // Dopo una rimozione, contano solo le partite saltate dopo.
        const finestra = date.slice(0, i + 1).filter((x) => x > da && (toltoIl === null || x > toltoIl));
        if (finestra.length >= regola.quanti) {
            inizio = d;
            quali = finestra;
        }
    }
    if (inizio === null)
        return { attivo: false, fino: null, date: [] };
    const fino = piuMesi(inizio, regola.mesiCaparra);
    return { attivo: oggi < fino, fino, date: quali };
}
/**
 * ↩️ Voce 30 — le partite saltate negli ultimi `mesi` mesi, fino a oggi: quelle
 * che la scheda del socio mostra anche sotto la soglia. Stessa finestra di
 * `bloccoDaiNoShow` (esclusa la data di `mesi` mesi fa, incluso oggi).
 */
export function saltateRecenti(saltate, oggi, mesi) {
    const da = piuMesi(oggi, -mesi);
    return [...saltate]
        .filter((x) => x > da && x <= oggi)
        .sort()
        .reverse();
}
/** R12 nella scheda della prenotazione: quanti mancano, e quanto va all'organizzatore. */
export const quotaMancanteSchema = z.object({
    missing: z.number().int(),
    quotaCents: z.number().int(),
    organizerTotalCents: z.number().int(),
    organizerMemberId: z.uuid(),
    organizerName: z.string(),
    done: z.boolean(),
});
/** 🆕 Fase 2 — la partita vista dalla Segreteria: chi manca, il no-show, il lucchetto dell'organizzatore. */
export const organizzatoreDellaPartitaSchema = z.object({
    missing: quotaMancanteSchema.nullable(),
    noShow: z.boolean(),
    organizerFlag: bloccoNoShowSchema.nullable(),
    /** 🆕 Voce 30 — l'ultimo no-show annullato su questa partita, se adesso non è segnata. */
    noShowCancelled: annulloNoShowSchema.nullable().default(null),
    /**
     * 🆕 Voce 30 — cosa succede annullando il no-show vivo di questa partita:
     * quanti ne restano all'organizzatore, la soglia, e se il lucchetto cade.
     * Nullo quando non c'è un no-show da annullare che conti per il lucchetto.
     */
    cancelPreview: z
        .object({ remaining: z.number().int(), threshold: z.number().int(), liftsFlag: z.boolean() })
        .nullable()
        .default(null),
});
// ── 🆕 Fase 3 (R6): la trattenuta al maestro ─────────────────────────────────
/** Una lezione della sera disdetta tardi, o l'allievo che non è venuto: 30 € dal compenso. */
export const trattenutaDelMaestroSchema = z.object({
    id: z.uuid(),
    instructorId: z.uuid(),
    date: dataSchema,
    time: z.string(),
    courtName: z.string(),
    students: z.string(),
    reason: z.enum(['disdetta_tardiva', 'no_show']),
    hoursBefore: z.number().int().nullable(),
    amountCents: z.number().int(),
});
/** Il credito che scade, nel borsellino. */
export const creditoInScadenzaSchema = z.object({
    amountCents: z.number().int(),
    expiresOn: dataSchema,
});
const MESI_CON_ANNO = [
    'gennaio',
    'febbraio',
    'marzo',
    'aprile',
    'maggio',
    'giugno',
    'luglio',
    'agosto',
    'settembre',
    'ottobre',
    'novembre',
    'dicembre',
];
/** «25 settembre 2027»: la scadenza di un credito, che sta sempre un anno più in là. */
export function dataConAnno(data) {
    const [a, m, g] = data.split('-');
    return `${Number(g)} ${MESI_CON_ANNO[Number(m) - 1]} ${a}`;
}
// ── 🆕 Fase 3 (R9): il banco e il lettore ────────────────────────────────────
export const incassaAlBancoSchema = z.object({ method: z.enum(['cash', 'card']) });
export const collegaLettoreSchema = z.object({
    /** Il codice che il Solo mostra sullo schermo: 8 o 9 lettere e cifre. */
    pairingCode: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9]{8,9}$/, 'Il codice del lettore ha 8 o 9 lettere e cifre'),
    name: z.string().trim().min(1).max(40).default('Banco'),
});
export const esitoDelLettoreSchema = z.object({
    /** `attende` la carta, `pagata`, `rifiutata` (la carta), `annullata` (sul lettore). */
    outcome: z.enum(['attende', 'pagata', 'rifiutata', 'annullata']),
    deposit: caparraDellaSegreteriaSchema,
});
// ── 🆕 Fase 3 (R5): cambia organizzatore ────────────────────────────────────
export const STATI_CAMBIO = ['pending', 'accepted', 'done', 'declined', 'expired', 'withdrawn'];
export const cambioOrganizzatoreSchema = z.object({
    id: z.uuid(),
    bookingId: z.uuid(),
    fromMemberId: z.uuid(),
    fromName: z.string(),
    toMemberId: z.uuid(),
    toName: z.string(),
    /** Il primo organizzatore lascia il suo posto in campo al nuovo. */
    leavesPlace: z.boolean(),
    via: z.enum(['bot', 'staff']),
    status: z.enum(STATI_CAMBIO),
    depositId: z.uuid().nullable(),
    expiresAt: z.string(),
    date: dataSchema,
    time: oraSchema,
    courtName: z.string(),
});
/** Dalla Segreteria: chi, se prende il posto del primo, e come paga la caparra nuova (di sera). */
export const cambiaOrganizzatoreSchema = z.object({
    toMemberId: z.uuid(),
    leavesPlace: z.boolean().default(false),
    caparra: z.enum(['link', 'cash', 'card']).nullable().default(null),
});
/** Dal bot: chi chiede, a chi, e se gli lascia il posto. */
export const chiediCambioSchema = z.object({
    memberId: z.uuid(),
    toMemberId: z.uuid(),
    leavesPlace: z.boolean().default(false),
});
export const rispostaCambioSchema = z.object({
    memberId: z.uuid(),
    accept: z.boolean(),
    /** Dentro le 48 ore, il «sì» esplicito alla caparra che non si rimborsa. */
    acceptNonRefundable: z.boolean().default(false),
});
export const esitoCambioSchema = z.object({
    outcome: z.enum(['fatto', 'rifiutato', 'non_lo_so']),
    reason: z.string().nullable(),
    /** Il codice del rifiuto, quando il bot deve fare una domanda (regolamento, non rimborsabile). */
    code: z.string().nullable().default(null),
    change: cambioOrganizzatoreSchema.nullable(),
    deposit: caparraDelPonteSchema.nullable(),
});
// ── 🆕 Fase 3 (R7): la caparra del campo fisso ──────────────────────────────
export const caparraDelFissoSchema = z.discriminatedUnion('modo', [
    z.object({ modo: z.literal('link') }),
    z.object({ modo: z.literal('banco'), metodo: z.enum(['cash', 'card']) }),
    z.object({ modo: z.literal('lettore') }),
    z.object({ modo: z.literal('esente'), motivo: z.string().trim().min(3, 'Scrivi il motivo').max(200) }),
]);
// ── 🆕 Fase 3: i resoconti (Cassa → 🌙 Caparre) ──────────────────────────────
export const periodoSchema = z
    .object({ from: dataSchema, to: dataSchema })
    .refine((p) => p.from <= p.to, { message: 'La data d’inizio viene dopo quella di fine', path: ['to'] });
export const rigaResocontoSchema = z.object({
    depositId: z.uuid(),
    bookingId: z.uuid(),
    date: dataSchema,
    time: oraSchema,
    courtName: z.string(),
    memberName: z.string(),
    /** La caparra di un campo fisso: vale per tutte le sue partite. */
    series: z.boolean(),
    status: statoCaparraSchema,
    paidWith: comePagataSchema.nullable(),
    closeReason: z.string().nullable(),
    amountCents: z.number().int(),
    creditCents: z.number().int(),
    cardCents: z.number().int(),
    transactionCode: z.string().nullable(),
});
export const esenzioneSchema = z.object({
    date: dataSchema.nullable(),
    time: oraSchema.nullable(),
    courtName: z.string().nullable(),
    what: z.string(),
    reason: z.string(),
    by: z.string().nullable(),
});
export const resocontoCaparreSchema = z.object({
    from: dataSchema,
    to: dataSchema,
    rows: z.array(rigaResocontoSchema),
    exemptions: z.array(esenzioneSchema),
    deductions: z.array(trattenutaDelMaestroSchema.extend({ instructorName: z.string() })),
});
/** Un pagamento di SumUp che non corrisponde a nessuna caparra. */
export const sumupDaAbbinareSchema = z.object({
    /** Nullo quando SumUp non si può leggere: il perché, per la Segreteria. */
    unavailable: z.string().nullable(),
    items: z.array(z.object({
        transactionCode: z.string(),
        amountCents: z.number().int(),
        at: z.string(),
        paymentType: z.string().nullable(),
    })),
});
/** Com'è finita, detto alla Segreteria (per la tabella e per il CSV). */
export const ETICHETTE_CHIUSURA = {
    giocata: 'Scalato dalla partita',
    disdetta_in_tempo: 'Disdetta in tempo',
    disdetta_tardiva: 'Disdetta tardiva',
    no_show: 'Campo vuoto (no-show)',
    circolo: 'Annullata dal circolo',
    annullata: 'Annullata prima di pagare',
    tempo_scaduto: 'Tempo scaduto',
    pagamento_tardivo: 'Pagato in ritardo',
    organizzatore_cambiato: 'Organizzatore cambiato',
    fisso_finito: 'Campo fisso finito',
    cambio_non_fatto: 'Cambio d’organizzatore non fatto',
};
export function comeEFinita(closeReason) {
    if (closeReason === null)
        return '';
    if (closeReason.startsWith('a_discrezione: '))
        return `A discrezione: ${closeReason.slice(15)}`;
    return ETICHETTE_CHIUSURA[closeReason] ?? closeReason;
}
// ── 🔔 Il campo vuoto da segnare (voce 49, 27/09/2026, mockup approvato: «1 sì, 2 A, 3 A») ──
/**
 * Una partita con l'acconto, finita, di cui non risulta incassato niente oltre
 * l'acconto: si è giocata o il campo era vuoto? Finché la Segreteria non lo
 * dice, l'acconto resta aperto (scelta 2A) — e il programma non decide niente.
 */
export const partitaDaSegnareSchema = z.object({
    bookingId: z.uuid(),
    depositId: z.uuid(),
    date: dataSchema,
    time: oraSchema,
    courtName: z.string(),
    /** Chi ha organizzato, o chi ha il campo fisso: il nome per la Segreteria. */
    organizerName: z.string(),
    amountCents: z.number().int(),
    paidWith: comePagataSchema.nullable(),
    players: z.number().int(),
    /** La partita di un campo fisso: l'acconto è quello del fisso, uno per tutte. */
    seriesId: z.uuid().nullable(),
});
export const daSegnareSchema = z.object({ partite: z.array(partitaDaSegnareSchema) });
//# sourceMappingURL=caparra.js.map